import type { tFormPrompts } from '../../types';

const DRAFT_KEY_PREFIX = 'formBuilderDraft:';
const DRAFT_VERSION = 1;

export type FormBuilderDraftSnapshot = {
    version: number;
    savedAt: number;
    formName: string;
    formContents: tFormPrompts[];
    noMachineUMCounter: boolean;
    alwaysTriggerEmail: boolean;
    formTypeSelection: string;
    equipmentType: string;
    umCounterLabel: string;
    persistedFormId?: string;
    /** True when this draft started from “Create a new copy”. */
    isFormCopy?: boolean;
    initialFormState: {
        name: string;
        contents: string;
        noUM: boolean;
        email: boolean;
        formType: string;
        equipmentType: string;
        umCounterLabel: string;
    };
};

export function formBuilderDraftKey(input: {
    companyId?: string | null;
    userId?: string | null;
    formId?: string | null;
    templateId?: string | null;
    isNewForm?: boolean;
    isEditTemplateMode?: boolean;
    formType?: string | null;
}): string {
    const company = input.companyId || 'no-company';
    const owner = input.userId ? `${company}:${input.userId}` : company;
    if (input.isEditTemplateMode && input.templateId) {
        return `${DRAFT_KEY_PREFIX}${owner}:template:${input.templateId}`;
    }
    if (input.formId) {
        return `${DRAFT_KEY_PREFIX}${owner}:edit:${input.formId}`;
    }
    if (input.isNewForm) {
        const formType = input.formType?.trim() || 'unknown';
        return `${DRAFT_KEY_PREFIX}${owner}:new:${formType}`;
    }
    return `${DRAFT_KEY_PREFIX}${owner}:scratch`;
}

/** Untyped `:new` key used before form type was part of the draft identity. */
export function formBuilderLegacyNewDraftKey(companyId?: string | null): string {
    return `${DRAFT_KEY_PREFIX}${companyId || 'no-company'}:new`;
}

export function clearFormBuilderDraft(key: string): void {
    try {
        sessionStorage.removeItem(key);
    } catch {
        // ignore
    }
}

/** Clears new-form drafts so a published form cannot reopen in Add New. */
export function clearFormBuilderNewFormDrafts(
    companyId: string | null | undefined,
    formType?: string | null,
    userId?: string | null,
): void {
    if (userId) {
        clearFormBuilderDraft(formBuilderDraftKey({ companyId, userId, isNewForm: true, formType }));
    }
    clearFormBuilderDraft(formBuilderDraftKey({ companyId, isNewForm: true, formType }));
    clearFormBuilderDraft(formBuilderLegacyNewDraftKey(companyId));
    if (companyId) {
        if (userId) {
            clearFormBuilderDraft(
                formBuilderDraftKey({ companyId: null, userId, isNewForm: true, formType }),
            );
        }
        clearFormBuilderDraft(formBuilderDraftKey({ companyId: null, isNewForm: true, formType }));
        clearFormBuilderDraft(formBuilderLegacyNewDraftKey(null));
    }
}

export function readFormBuilderDraft(key: string): FormBuilderDraftSnapshot | null {
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as FormBuilderDraftSnapshot;
        if (parsed?.version !== DRAFT_VERSION || !Array.isArray(parsed.formContents)) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

export function writeFormBuilderDraft(key: string, draft: Omit<FormBuilderDraftSnapshot, 'version' | 'savedAt'>): void {
    try {
        const payload: FormBuilderDraftSnapshot = {
            ...draft,
            version: DRAFT_VERSION,
            savedAt: Date.now(),
        };
        sessionStorage.setItem(key, JSON.stringify(payload));
    } catch {
        // Quota / private mode — draft persistence is best-effort.
    }
}
